import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ADMIN_API_URL } from '../../constants';
import showToast from '../../utils/toast';

const Dashboard = () => {
    const navigate = useNavigate();
    
    // State management
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    
    // Calculate default start date (30 days ago from today)
    const getDefaultStartDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };

    // Filter states with default values
    const [startDate, setStartDate] = useState(getDefaultStartDate());
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [source, setSource] = useState('Direct');
    const [utmCampaign, setUtmCampaign] = useState('SnapFunds');
    const [leadStatus, setLeadStatus] = useState('');

    // Normalize ADMIN_API_URL to avoid double slashes and relative paths
    const apiBase = (typeof ADMIN_API_URL === 'string' ? ADMIN_API_URL : '').replace(/\/+$/, '');

    useEffect(() => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
        // Fetch analytics data on component mount
        console.log('Dashboard mounted, fetching analytics...');
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    // Debug: Log when analyticsData changes
    useEffect(() => {
        console.log('Analytics data changed:', analyticsData);
    }, [analyticsData]);

    // Fetch analytics from backend
    const fetchAnalytics = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = sessionStorage.getItem("authToken");
            const queryParams = new URLSearchParams({
                startDate,
                endDate,
                source,
                utmCampaign,
                ...(leadStatus && { leadStatus })
            });

            console.log('Fetching analytics with URL:', `${apiBase}/dashboard-analytics?${queryParams}`);
            console.log('ADMIN_API_URL:', ADMIN_API_URL);
            console.log('Token:', token ? 'Present' : 'Missing');
            
            // Check if ADMIN_API_URL is properly set
            if (!ADMIN_API_URL) {
                throw new Error('ADMIN_API_URL is not configured');
            }

            const response = await fetch(`${apiBase}/dashboard-analytics?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            console.log('Response status:', response.status);
            const result = await response.json();
            console.log('API Response:', result);

            if (result.status === 200) {
                setAnalyticsData(result.data);
                console.log('Analytics data set:', result.data);
            } else {
                console.error('API Error:', result);
                setError(result.message || 'Failed to fetch analytics data.');
                
                // Fallback: Set mock data for testing
                console.log('Setting fallback mock data...');
                setAnalyticsData({
                    cashe: {
                        leadCreated: { total: 43, otpVerified: 40, eligible: 35, notEligible: 5 },
                        eligibilityChecked: { total: 35, otpVerified: 35, eligible: 30, notEligible: 5 },
                        personalDetailsFilled: { total: 30, otpVerified: 30, eligible: 25, notEligible: 5 },
                        employmentDetailsFilled: { total: 25, otpVerified: 25, eligible: 20, notEligible: 5 },
                        casheCustomerCreated: { total: 20, otpVerified: 20, eligible: 18, notEligible: 2 }
                    },
                    trustpaisa: {
                        leadCreated: { total: 25, otpVerified: 22, eligible: 20, notEligible: 2 },
                        eligibilityChecked: { total: 20, otpVerified: 20, eligible: 18, notEligible: 2 },
                        personalDetailsFilled: { total: 18, otpVerified: 18, eligible: 15, notEligible: 3 },
                        employmentDetailsFilled: { total: 15, otpVerified: 15, eligible: 12, notEligible: 3 },
                        casheCustomerCreated: { total: 12, otpVerified: 12, eligible: 10, notEligible: 2 }
                    },
                    unassigned: {
                        leadCreated: { total: 100, otpVerified: 80, eligible: 60, notEligible: 20 },
                        eligibilityChecked: { total: 60, otpVerified: 60, eligible: 45, notEligible: 15 },
                        personalDetailsFilled: { total: 45, otpVerified: 45, eligible: 35, notEligible: 10 },
                        employmentDetailsFilled: { total: 35, otpVerified: 35, eligible: 25, notEligible: 10 },
                        casheCustomerCreated: { total: 25, otpVerified: 25, eligible: 20, notEligible: 5 }
                    }
                });
            }
        } catch (err) {
            console.error('Error fetching analytics:', err);
            setError('Failed to fetch analytics data. Please try again.');
            
            // Fallback: Set mock data for testing
            console.log('Setting fallback mock data due to error...');
            setAnalyticsData({
                cashe: {
                    leadCreated: { total: 43, otpVerified: 40, eligible: 35, notEligible: 5 },
                    eligibilityChecked: { total: 35, otpVerified: 35, eligible: 30, notEligible: 5 },
                    personalDetailsFilled: { total: 30, otpVerified: 30, eligible: 25, notEligible: 5 },
                    employmentDetailsFilled: { total: 25, otpVerified: 25, eligible: 20, notEligible: 5 },
                    casheCustomerCreated: { total: 20, otpVerified: 20, eligible: 18, notEligible: 2 }
                },
                trustpaisa: {
                    leadCreated: { total: 25, otpVerified: 22, eligible: 20, notEligible: 2 },
                    eligibilityChecked: { total: 20, otpVerified: 20, eligible: 18, notEligible: 2 },
                    personalDetailsFilled: { total: 18, otpVerified: 18, eligible: 15, notEligible: 3 },
                    employmentDetailsFilled: { total: 15, otpVerified: 15, eligible: 12, notEligible: 3 },
                    casheCustomerCreated: { total: 12, otpVerified: 12, eligible: 10, notEligible: 2 }
                },
                unassigned: {
                    leadCreated: { total: 100, otpVerified: 80, eligible: 60, notEligible: 20 },
                    eligibilityChecked: { total: 60, otpVerified: 60, eligible: 45, notEligible: 15 },
                    personalDetailsFilled: { total: 45, otpVerified: 45, eligible: 35, notEligible: 10 },
                    employmentDetailsFilled: { total: 35, otpVerified: 35, eligible: 25, notEligible: 10 },
                    casheCustomerCreated: { total: 25, otpVerified: 25, eligible: 20, notEligible: 5 }
                }
            });
        } finally {
            setLoading(false);
        }
    };

    // Convert JSON to CSV
    const convertToCSV = (data) => {
        if (!data || data.length === 0) return '';

        // Define headers (exactly as per backend CSV format)
        const headers = [
            'Sr No',
            'Lead ID',
            'Customer Name',
            'Email',
            'Mobile',
            'Lender ID',
            'Lender Name',
            'Application Stage',
            'Product Type',
            'Loan Amount',
            'Mobile Verified',
            'Is Eligible',
            'Lead Status',
            'Remarks',
            'Partner Customer ID',
            'Product Type Name',
            'Lead Created At',
            'Loan Request Created At',
            'Source',
            'UTM Campaign'
        ];

        // Create CSV rows
        const rows = data.map(row => [
            row.sr_no || '',
            row.id || '',
            row.name || 'N/A',
            row.email || 'N/A',
            row.mobile || '',
            row.lenderId || '',
            row.lenderName || '',
            row.applicationStage || '',
            row.productType || '',
            row.loanAmount || '',
            row.isMobileVerified ? 'Yes' : 'No',
            row.isEligible === true ? 'Yes' : row.isEligible === false ? 'No' : 'N/A',
            row.leadStatus || '',
            row.remarks || 'N/A',
            row.partnerCustomerId || 'N/A',
            row.productTypeName || '',
            row.leadCreatedAt || 'N/A',
            row.loanRequestCreatedAt || 'N/A',
            row.source || '',
            row.utmCampaign || ''
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => {
                // Escape cells containing commas, quotes, or newlines
                const cellStr = String(cell);
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(','))
        ].join('\n');

        return csvContent;
    };

    // Handle CSV download
    const handleDownloadCSV = async () => {
        try {
            const token = sessionStorage.getItem("authToken");
            const queryParams = new URLSearchParams({
                startDate,
                endDate,
                source,
                utmCampaign,
                ...(leadStatus && { leadStatus })
            });

            const response = await fetch(`${apiBase}/dashboard-analytics/csv?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            if (response.ok) {
                // Check Content-Type to determine if response is JSON or CSV
                const contentType = response.headers.get('content-type');
                
                if (contentType && contentType.includes('application/json')) {
                    // Backend returning JSON format with data array
                    const result = await response.json();
                    
                    if (result.status === 200 && result.data && result.data.length > 0) {
                        // Convert JSON to CSV
                        const csvContent = convertToCSV(result.data);
                        
                        // Create blob and download
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `lead-details-${startDate}-to-${endDate}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        
                        showToast(`Successfully downloaded ${result.totalRecords} leads data!`, 'success');
                    } else {
                        showToast('No data available to download.', 'warning');
                    }
                } else {
                    // Backend returning CSV text directly
                    const csvContent = await response.text();
                    
                    if (csvContent && csvContent.trim().length > 0) {
                        // Create blob and download
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `lead-details-${startDate}-to-${endDate}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        
                        // Count rows for success message (excluding header)
                        const rowCount = csvContent.split('\n').length - 1;
                        showToast(`Successfully downloaded ${rowCount} leads data!`, 'success');
                    } else {
                        showToast('No data available to download.', 'warning');
                    }
                }
            } else {
                showToast('Failed to download CSV. Please try again.', 'error');
            }
        } catch (err) {
            console.error('Error downloading CSV:', err);
            showToast('Failed to download CSV. Please try again.', 'error');
        }
    };

    // Handle filter apply
    const handleApplyFilters = () => {
        fetchAnalytics();
    };

    // Calculate totals for each lender
    const calculateLenderTotals = (lenderData) => {
        if (!lenderData) return { total: 0, otpVerified: 0, eligible: 0, notEligible: 0 };
        
        return Object.values(lenderData).reduce((acc, stage) => ({
            total: acc.total + stage.total,
            otpVerified: acc.otpVerified + stage.otpVerified,
            eligible: acc.eligible + stage.eligible,
            notEligible: acc.notEligible + stage.notEligible
        }), { total: 0, otpVerified: 0, eligible: 0, notEligible: 0 });
    };

    // Prepare data for charts
    const prepareChartData = () => {
        if (!analyticsData) return [];

        const stages = ['leadCreated', 'eligibilityChecked', 'personalDetailsFilled', 'employmentDetailsFilled', 'casheCustomerCreated'];
        
        return stages.map(stage => ({
            stage: formatStageName(stage),
            CASHe: analyticsData.cashe[stage]?.total || 0,
            TrustPaisa: analyticsData.trustpaisa[stage]?.total || 0,
            Unassigned: analyticsData.unassigned[stage]?.total || 0
        }));
    };

    // Format stage names for display
    const formatStageName = (stage) => {
        const stageNames = {
            'leadCreated': 'Lead Created',
            'eligibilityChecked': 'Eligibility Checked',
            'personalDetailsFilled': 'Personal Details',
            'employmentDetailsFilled': 'Employment Details',
            'casheCustomerCreated': 'Customer Created'
        };
        return stageNames[stage] || stage;
    };

    const leadStatuses = [
        "New", "Contacted", "Unable to connect", "Interested", 
        "Awaiting Customer Details", "Awaiting Lender Update", 
        "Approved", "Disbursed", "Rejected", "Dropped Off", 
        "Invalid", "Did not Apply", "Loan Closed", "Loan Active", "EMI Overdue"
    ];

    return (
        <>
            <Header />
            <div id="wrapper">
                <div className="normalheader">
                    <div className="hpanel">
                        <div className="panel-body">
                            <h2 className="font-light m-b-xs">
                                <i className="fa fa-dashboard"></i> Dashboard & Reporting
                            </h2>
                            <p style={{ color: '#7c7c7c', fontSize: '14px' }}>
                                Comprehensive analytics for lead tracking and performance monitoring
                            </p>
                        </div>
                    </div>
                </div>
                <div className="content">
                    {/* Filters Section */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                <div className="panel-heading" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: '8px 8px 0 0' }}>
                                    <h3 style={{ margin: '0', fontSize: '16px', fontWeight: '600' }}>
                                        <i className="fa fa-filter"></i> Filter Analytics Data
                                    </h3>
                                </div>
                                <div className="panel-body" style={{ padding: '25px' }}>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={{ fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                                                    Start Date <span style={{ color: '#e74c3c' }}>*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                    style={{ borderRadius: '6px', border: '1px solid #ddd' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={{ fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                                                    End Date <span style={{ color: '#e74c3c' }}>*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    style={{ borderRadius: '6px', border: '1px solid #ddd' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label style={{ fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                                                    Source
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={source}
                                                    onChange={(e) => setSource(e.target.value)}
                                                    placeholder="e.g., Direct"
                                                    style={{ borderRadius: '6px', border: '1px solid #ddd' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label style={{ fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                                                    UTM Campaign
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={utmCampaign}
                                                    onChange={(e) => setUtmCampaign(e.target.value)}
                                                    placeholder="e.g., SnapFunds"
                                                    style={{ borderRadius: '6px', border: '1px solid #ddd' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label style={{ fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                                                    Lead Status
                                                </label>
                                                <select
                                                    className="form-control"
                                                    value={leadStatus}
                                                    onChange={(e) => setLeadStatus(e.target.value)}
                                                    style={{ borderRadius: '6px', border: '1px solid #ddd' }}
                                                >
                                                    <option value="">All Statuses</option>
                                                    {leadStatuses.map(status => (
                                                        <option key={status} value={status}>{status}</option>
                                                ))}
                                            </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right" style={{ marginTop: '15px' }}>
                                        <button 
                                            className="btn btn-primary" 
                                            onClick={handleApplyFilters}
                                            disabled={loading}
                                            style={{ 
                                                borderRadius: '6px', 
                                                padding: '10px 30px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                border: 'none',
                                                fontWeight: '600',
                                                marginRight: '10px'
                                            }}
                                        >
                                            <i className="fa fa-refresh"></i> {loading ? 'Loading...' : 'Apply Filters'}
                                        </button>
                                        <button 
                                            className="btn btn-success" 
                                            onClick={handleDownloadCSV}
                                            disabled={!analyticsData || loading}
                                            style={{ 
                                                borderRadius: '6px', 
                                                padding: '10px 30px',
                                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                                border: 'none',
                                                fontWeight: '600'
                                            }}
                                        >
                                            <i className="fa fa-download"></i> Download CSV
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                    <div className="row">
                            <div className="col-lg-12">
                                <div className="alert alert-danger" style={{ borderRadius: '6px' }}>
                                    <i className="fa fa-exclamation-circle"></i> {error}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Loading State */}
                    {loading && (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="text-center" style={{ padding: '60px 0' }}>
                                    <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#667eea' }}></i>
                                    <p style={{ marginTop: '20px', fontSize: '16px', color: '#7c7c7c' }}>Loading analytics data...</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Dashboard Content */}
                    {!loading && (
                        <>
                            {/* Key Metrics Summary commented out (hidden for now)
                            <div className="row" style={{ marginTop: '25px' }}>
                                ...cards removed...
                            </div>
                            */}

                            {/* Stage-wise Analytics Chart */}
                            {analyticsData && (
                                <div className="row" style={{ marginTop: '25px' }}>
                                    <div className="col-lg-12">
                                        <div className="hpanel" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                                            <div className="panel-heading" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: '12px 12px 0 0', padding: '15px 20px' }}>
                                                <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600' }}>
                                                    <i className="fa fa-bar-chart"></i> Stage-wise Lead Distribution
                                                </h3>
                                                <small style={{ opacity: '0.9' }}>Comparison across all lenders by application stage</small>
                                            </div>
                                            <div className="panel-body" style={{ padding: '30px', minHeight: '450px' }}>
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <BarChart data={prepareChartData()}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                        <XAxis 
                                                            dataKey="stage" 
                                                            angle={-15}
                                                            textAnchor="end"
                                                            height={80}
                                                            style={{ fontSize: '12px' }}
                                                        />
                                                        <YAxis style={{ fontSize: '12px' }} />
                                                        <Tooltip 
                                                            contentStyle={{ 
                                                                backgroundColor: 'rgba(255,255,255,0.95)', 
                                                                border: '1px solid #ddd',
                                                                borderRadius: '8px',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                            }}
                                                        />
                                                        <Legend 
                                                            wrapperStyle={{ paddingTop: '20px' }}
                                                            iconType="circle"
                                                        />
                                                        <Bar dataKey="CASHe" fill="#667eea" radius={[8, 8, 0, 0]} />
                                                        <Bar dataKey="TrustPaisa" fill="#11998e" radius={[8, 8, 0, 0]} />
                                                        <Bar dataKey="Unassigned" fill="#f093fb" radius={[8, 8, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                                </div>
                            )}

                            {/* Detailed Stage-wise Tables */}
                            {analyticsData && (
                                <div className="row" style={{ marginTop: '25px' }}>
                                    {/* CASHe Table */}
                                    <div className="col-lg-4 col-md-12">
                                        <div className="hpanel" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                                            <div className="panel-heading" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: '12px 12px 0 0', padding: '12px 20px' }}>
                                                <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '600' }}>
                                                    <i className="fa fa-university"></i> CASHe Details
                                                </h4>
                                            </div>
                                            <div className="panel-body" style={{ padding: '0' }}>
                                                <div className="table-responsive">
                                                    <table className="table table-hover" style={{ marginBottom: '0' }}>
                                                        <thead style={{ background: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ fontSize: '12px', fontWeight: '600', padding: '12px' }}>Stage</th>
                                                                <th style={{ fontSize: '12px', fontWeight: '600', padding: '12px', textAlign: 'center' }}>Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Object.keys(analyticsData.cashe).map((stage, index) => (
                                                                <tr key={index}>
                                                                    <td style={{ fontSize: '12px', padding: '12px' }}>{formatStageName(stage)}</td>
                                                                    <td style={{ fontSize: '13px', fontWeight: '600', textAlign: 'center', padding: '12px' }}>
                                                                        {analyticsData.cashe[stage].total}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* TrustPaisa Table */}
                                    <div className="col-lg-4 col-md-12">
                                        <div className="hpanel" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                                            <div className="panel-heading" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderRadius: '12px 12px 0 0', padding: '12px 20px' }}>
                                                <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '600' }}>
                                                    <i className="fa fa-handshake-o"></i> TrustPaisa Details
                                                </h4>
                                            </div>
                                            <div className="panel-body" style={{ padding: '0' }}>
                                                <div className="table-responsive">
                                                    <table className="table table-hover" style={{ marginBottom: '0' }}>
                                                        <thead style={{ background: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ fontSize: '12px', fontWeight: '600', padding: '12px' }}>Stage</th>
                                                                <th style={{ fontSize: '12px', fontWeight: '600', padding: '12px', textAlign: 'center' }}>Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Object.keys(analyticsData.trustpaisa).map((stage, index) => (
                                                                <tr key={index}>
                                                                    <td style={{ fontSize: '12px', padding: '12px' }}>{formatStageName(stage)}</td>
                                                                    <td style={{ fontSize: '13px', fontWeight: '600', textAlign: 'center', padding: '12px' }}>
                                                                        {analyticsData.trustpaisa[stage].total}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                            </div>
                        </div>
                    </div>

                                    {/* Unassigned Table */}
                                    <div className="col-lg-4 col-md-12">
                                        <div className="hpanel" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                                            <div className="panel-heading" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', borderRadius: '12px 12px 0 0', padding: '12px 20px' }}>
                                                <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '600' }}>
                                                    <i className="fa fa-users"></i> Unassigned Details
                                                </h4>
                                </div>
                                            <div className="panel-body" style={{ padding: '0' }}>
                                    <div className="table-responsive">
                                                    <table className="table table-hover" style={{ marginBottom: '0' }}>
                                                        <thead style={{ background: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ fontSize: '12px', fontWeight: '600', padding: '12px' }}>Stage</th>
                                                                <th style={{ fontSize: '12px', fontWeight: '600', padding: '12px', textAlign: 'center' }}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                            {Object.keys(analyticsData.unassigned).map((stage, index) => (
                                                    <tr key={index}>
                                                                    <td style={{ fontSize: '12px', padding: '12px' }}>{formatStageName(stage)}</td>
                                                                    <td style={{ fontSize: '13px', fontWeight: '600', textAlign: 'center', padding: '12px' }}>
                                                                        {analyticsData.unassigned[stage].total}
                                                                    </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                            )}
                        </>
                    )}
                </div>
                <Footer />
            </div>
        </>
    );
};

export default Dashboard; 